// IndexedDB Storage Adapter - Unlimited storage replacement for localStorage
// Maintains same interface as StorageService for easy migration

class IndexedDBAdapter {
  constructor() {
    this.dbName = 'BibleAppDB';
    this.version = 1;
    this.stores = {
      workflows: 'bible_workflows',
      preferences: 'bible_ui_preferences',
      enhancements: 'bible_enhancement_settings',
      videos: 'video_positions',
      autosave: 'autosave_data'
    };
    this.db = null;
  }

  // Initialize IndexedDB connection
  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object stores (tables)
        Object.values(this.stores).forEach(storeName => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: 'key' });
            store.createIndex('timestamp', 'timestamp', { unique: false });
          }
        });
      };
    });
  }

  // Generic get method
  async getItem(storeName, key) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
    });
  }

  // Generic set method
  async setItem(storeName, key, value) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put({
        key,
        value,
        timestamp: Date.now()
      });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(true);
    });
  }

  // Generic remove method
  async removeItem(storeName, key) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(true);
    });
  }

  // Get all items from a store
  async getAllFromStore(storeName) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const items = request.result.reduce((acc, item) => {
          acc[item.key] = item.value;
          return acc;
        }, {});
        resolve(items);
      };
    });
  }

  // Clear entire store
  async clearStore(storeName) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(true);
    });
  }

  // Get storage usage estimate
  async getStorageEstimate() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      return await navigator.storage.estimate();
    }
    return { quota: 0, usage: 0 };
  }

  // Workflow-specific methods (maintaining localStorage interface)
  async getWorkflows() {
    const data = await this.getItem(this.stores.workflows, 'bible_workflows');
    return data ? JSON.parse(data) : [];
  }

  async setWorkflows(workflows) {
    const dataToStore = typeof workflows === 'string' ? workflows : JSON.stringify(workflows);
    return await this.setItem(this.stores.workflows, 'bible_workflows', dataToStore);
  }

  // UI Preferences methods
  async getUIPreferences() {
    const data = await this.getItem(this.stores.preferences, 'bible_ui_preferences');
    return data; // Return the raw data (JSON string) for migration compatibility
  }

  async setUIPreferences(preferences) {
    const dataToStore = typeof preferences === 'string' ? preferences : JSON.stringify(preferences);
    return await this.setItem(this.stores.preferences, 'bible_ui_preferences', dataToStore);
  }

  // Enhancement Settings methods
  async getEnhancementSettings() {
    const data = await this.getItem(this.stores.enhancements, 'bible_enhancement_settings');
    return data; // Return the raw data (JSON string) for migration compatibility
  }

  async setEnhancementSettings(settings) {
    const dataToStore = typeof settings === 'string' ? settings : JSON.stringify(settings);
    return await this.setItem(this.stores.enhancements, 'bible_enhancement_settings', dataToStore);
  }

  // Video positions methods
  async getVideoPosition(videoKey) {
    return await this.getItem(this.stores.videos, videoKey);
  }

  async setVideoPosition(videoKey, data) {
    const dataToStore = typeof data === 'string' ? data : JSON.stringify(data);
    return await this.setItem(this.stores.videos, videoKey, dataToStore);
  }

  async removeVideoPosition(videoKey) {
    return await this.removeItem(this.stores.videos, videoKey);
  }

  // Autosave methods
  async getAutosave(key) {
    return await this.getItem(this.stores.autosave, key);
  }

  async setAutosave(key, data) {
    const dataToStore = typeof data === 'string' ? data : JSON.stringify(data);
    return await this.setItem(this.stores.autosave, key, dataToStore);
  }

  async removeAutosave(key) {
    return await this.removeItem(this.stores.autosave, key);
  }

  // Migration helper methods
  async importFromLocalStorage() {
    const migrationTasks = [];

    // console.log('🔄 Starting data migration from localStorage...');

    // Migrate workflows - localStorage data is already JSON string
    const workflows = localStorage.getItem('bible_workflows');
    if (workflows) {
      // console.log('📋 Migrating workflows...');
      migrationTasks.push(this.setItem(this.stores.workflows, 'bible_workflows', workflows));
    }

    // Migrate UI preferences - localStorage data is already JSON string
    const preferences = localStorage.getItem('bible_ui_preferences');
    if (preferences) {
      // console.log('⚙️ Migrating UI preferences...');
      migrationTasks.push(this.setItem(this.stores.preferences, 'bible_ui_preferences', preferences));
    }

    // Migrate enhancement settings - localStorage data is already JSON string
    const enhancements = localStorage.getItem('bible_enhancement_settings');
    if (enhancements) {
      // console.log('🎨 Migrating enhancement settings...');
      migrationTasks.push(this.setItem(this.stores.enhancements, 'bible_enhancement_settings', enhancements));
    }

    // Migrate video positions
    let videoCount = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('video_') && key.includes('_positions')) {
        const data = localStorage.getItem(key);
        migrationTasks.push(this.setItem(this.stores.videos, key, data));
        videoCount++;
      }
    }
    if (videoCount > 0) {
      // console.log(`🎥 Migrating ${videoCount} video position entries...`);
    }

    // Migrate autosave data
    let autosaveCount = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('autosave_') || key === 'bible_workflow_autosave')) {
        const data = localStorage.getItem(key);
        migrationTasks.push(this.setItem(this.stores.autosave, key, data));
        autosaveCount++;
      }
    }
    if (autosaveCount > 0) {
      // console.log(`💾 Migrating ${autosaveCount} autosave entries...`);
    }

    await Promise.all(migrationTasks);
    // console.log('✅ Migration from localStorage to IndexedDB completed');
  }

  // Get all video position keys
  async getAllVideoKeys() {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.stores.videos], 'readonly');
      const store = transaction.objectStore(this.stores.videos);
      const request = store.getAllKeys();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  // Cleanup old localStorage data after successful migration
  clearLocalStorageAfterMigration() {
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key === 'bible_workflows' ||
        key === 'bible_ui_preferences' ||
        key === 'bible_enhancement_settings' ||
        key.includes('video_') ||
        key.includes('autosave_')
      )) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    // console.log('🧹 Cleaned up localStorage after migration');
  }
}

export default new IndexedDBAdapter();